import { useState, useMemo, useCallback } from 'react'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

interface PaisItem {
  pais: string
  id: string
  total: number
  en_base: number
  no_en_base: number
  porcentaje: number
}

interface MarcaItem {
  marca: string
  id: string
  total: number
  en_base: number
  no_en_base: number
  porcentaje: number
}

interface EspecialidadItem {
  especialidad: string
  total: number
  en_base: number
  no_en_base: number
  porcentaje: number
  estado: string
}

interface MedicoItem {
  nombre: string
  especialidad: string
  region: string
  marca: string
  pais: string
  px: number
  en_base: boolean
}

interface OpellaEnero2026Data {
  titulo: string
  subtitulo: string
  total_medicos: number
  total_en_base: number
  total_no_en_base: number
  porcentaje_en_base: number
  porcentaje_no_en_base: number
  total_regiones: number
  total_especialidades: number
  paises: PaisItem[]
  marcas: MarcaItem[]
  regiones: unknown[]
  especialidades: EspecialidadItem[]
  medicos_en_base: MedicoItem[]
  todos_medicos: MedicoItem[]
}

function getEstadoBadge(porcentaje: number) {
  if (porcentaje >= 10)
    return (
      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
        Alto
      </span>
    )
  if (porcentaje >= 5)
    return (
      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
        Medio
      </span>
    )
  if (porcentaje > 0)
    return (
      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
        Bajo
      </span>
    )
  return (
    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
      Sin Datos
    </span>
  )
}

export function OpellaEnero2026Page() {
  const { data, isLoading, error } = useDashReport<OpellaEnero2026Data>('opella/enero-2026')
  const [filtroPais, setFiltroPais] = useState<string>('todos')
  const [filtroMarca, setFiltroMarca] = useState<string>('todas')
  const [marcasOpen, setMarcasOpen] = useState(false)
  const [paisesOpen, setPaisesOpen] = useState(false)
  const [especialidadesOpen, setEspecialidadesOpen] = useState(false)

  // Get pais name from select value
  const getPaisNombre = useCallback(
    (paisId: string) => {
      if (!data || paisId === 'todos') return null
      const pais = data.paises.find((p) => p.id === paisId)
      return pais?.pais ?? null
    },
    [data],
  )

  // Filter all medicos
  const medicosFiltrados = useMemo(() => {
    if (!data) return []
    let filtrados = data.todos_medicos
    const paisNombre = getPaisNombre(filtroPais)
    if (paisNombre) {
      filtrados = filtrados.filter((m) => m.pais === paisNombre)
    }
    if (filtroMarca !== 'todas') {
      // find marca name from marcas options
      const marcasFiltradas = computeMarcasFromMedicos(filtrados)
      const marcaObj = marcasFiltradas.find((_, idx) => String(idx + 1) === filtroMarca)
      if (marcaObj) {
        filtrados = filtrados.filter((m) => m.marca === marcaObj.marca)
      }
    }
    return filtrados
  }, [data, filtroPais, filtroMarca, getPaisNombre])

  // Stats computed from filtered medicos
  const stats = useMemo(() => {
    const total = medicosFiltrados.length
    const enBase = medicosFiltrados.filter((m) => m.en_base).length
    const noEnBase = total - enBase
    const pctEnBase = total > 0 ? ((enBase / total) * 100).toFixed(2) : '0'
    const pctNoEnBase = total > 0 ? ((noEnBase / total) * 100).toFixed(2) : '0'
    return { total, enBase, noEnBase, pctEnBase, pctNoEnBase }
  }, [medicosFiltrados])

  // Titulo resumen
  const tituloResumen = useMemo(() => {
    const paisNombre = getPaisNombre(filtroPais)
    const marcas = computeMarcasFromMedicos(
      data
        ? filtroPais === 'todos'
          ? data.todos_medicos
          : data.todos_medicos.filter((m) => m.pais === getPaisNombre(filtroPais))
        : [],
    )
    const marcaObj =
      filtroMarca !== 'todas' ? marcas.find((_, idx) => String(idx + 1) === filtroMarca) : null

    if (paisNombre && marcaObj) return `${paisNombre} - ${marcaObj.marca}`
    if (paisNombre) return paisNombre
    if (marcaObj) return marcaObj.marca
    return 'Resumen General'
  }, [data, filtroPais, filtroMarca, getPaisNombre])

  // Compute marcas available for current pais filter
  const marcasOptions = useMemo(() => {
    if (!data) return []
    let medicos = data.todos_medicos
    const paisNombre = getPaisNombre(filtroPais)
    if (paisNombre) {
      medicos = medicos.filter((m) => m.pais === paisNombre)
    }
    return computeMarcasFromMedicos(medicos)
  }, [data, filtroPais, getPaisNombre])

  // Computed tables from filtered medicos
  const tablaMarcas = useMemo(() => {
    return computeMarcasFromMedicos(medicosFiltrados)
  }, [medicosFiltrados])

  const tablaPaises = useMemo(() => {
    return computePaisesFromMedicos(medicosFiltrados)
  }, [medicosFiltrados])

  const tablaEspecialidades = useMemo(() => {
    return computeEspecialidadesFromMedicos(medicosFiltrados)
  }, [medicosFiltrados])

  const hayFiltrosActivos = filtroPais !== 'todos' || filtroMarca !== 'todas'

  const limpiarFiltros = useCallback(() => {
    setFiltroPais('todos')
    setFiltroMarca('todas')
  }, [])

  const handlePaisChange = useCallback((value: string) => {
    setFiltroPais(value)
    setFiltroMarca('todas')
  }, [])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img
                src="/logo-tonal-negro.png"
                alt="Tonal"
                className="h-8 lg:h-10 w-auto mr-4 lg:mr-6"
              />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">
                  {data.titulo}
                </h1>
                <p className="text-sm lg:text-base text-gray-600">
                  Análisis de Médicos Mucosolvan, Bisolvon, Neomelubrina
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://enterprise.tonalstrategies.com/pricing/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
              >
                Calculador
                <svg
                  className="w-4 h-4 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
              <svg
                className="w-4 h-4 text-gray-500 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              Filtros
            </h3>
            {hayFiltrosActivos && (
              <button
                onClick={limpiarFiltros}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Limpiar filtros
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Filtro de País */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg
                  className="w-5 h-5 text-blue-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                  />
                </svg>
                País
              </label>
              <select
                value={filtroPais}
                onChange={(e) => handlePaisChange(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="todos">Todos los países</option>
                {data.paises.map((pais) => (
                  <option key={pais.id} value={pais.id}>
                    {pais.pais} ({pais.total} médicos)
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro de Marca */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-900 mb-2">
                <svg
                  className="w-5 h-5 text-indigo-600 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                Marca
              </label>
              <select
                value={filtroMarca}
                onChange={(e) => setFiltroMarca(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
              >
                <option value="todas">Todas las marcas</option>
                {marcasOptions.map((item, idx) => (
                  <option key={item.marca} value={String(idx + 1)}>
                    {item.marca} ({item.total} médicos)
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg
              className="w-5 h-5 text-blue-600 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <span>{tituloResumen}</span>
          </h2>

          {/* Totales */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">
                {stats.total.toLocaleString('es-EC')}
              </p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">
                {stats.enBase.toLocaleString('es-EC')}
              </p>
              <p className="text-sm text-gray-600 mt-1">En Base Tonal</p>
              <p className="text-xs text-green-600 font-semibold mt-1">{stats.pctEnBase}%</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-500">
                {stats.noEnBase.toLocaleString('es-EC')}
              </p>
              <p className="text-sm text-gray-600 mt-1">No en Base</p>
              <p className="text-xs text-gray-500 font-semibold mt-1">{stats.pctNoEnBase}%</p>
            </div>
          </div>
        </div>

        {/* Desglose por Marca */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setMarcasOpen(!marcasOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg
                  className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                <span className="text-base lg:text-lg">Análisis por Marca</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">
                  ({tablaMarcas.length} marcas)
                </span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${marcasOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </button>

          {marcasOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">
                      Marca
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      Total
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      En Base
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      No en Base
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      %
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tablaMarcas.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No hay datos para mostrar
                      </td>
                    </tr>
                  ) : (
                    tablaMarcas.map((marca) => (
                      <tr
                        key={marca.marca}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">
                          {marca.marca}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">
                          {marca.total}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-green-600">
                          {marca.en_base}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-500">
                          {marca.no_en_base}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-indigo-600">
                          {marca.porcentaje.toFixed(2)}%
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                          {getEstadoBadge(marca.porcentaje)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Desglose por País */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setPaisesOpen(!paisesOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-blue-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg
                  className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-base lg:text-lg">Análisis por País</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">
                  ({tablaPaises.length} países)
                </span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${paisesOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </button>

          {paisesOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">
                      País
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      Total
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      En Base
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      No en Base
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      %
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tablaPaises.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No hay datos para mostrar
                      </td>
                    </tr>
                  ) : (
                    tablaPaises.map((pais) => (
                      <tr
                        key={pais.pais}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">
                          {pais.pais}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">
                          {pais.total}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-green-600">
                          {pais.en_base}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-500">
                          {pais.no_en_base}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-indigo-600">
                          {pais.porcentaje.toFixed(2)}%
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                          {getEstadoBadge(pais.porcentaje)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Desglose por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setEspecialidadesOpen(!especialidadesOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg
                  className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                  />
                </svg>
                <span className="text-base lg:text-lg">Análisis por Especialidad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">
                  ({tablaEspecialidades.length} especialidades)
                </span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${especialidadesOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </button>

          {especialidadesOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">
                      Especialidad
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      Total
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      En Base
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      No en Base
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      %
                    </th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tablaEspecialidades.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No hay datos para mostrar
                      </td>
                    </tr>
                  ) : (
                    tablaEspecialidades.map((esp) => (
                      <tr
                        key={esp.especialidad}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">
                          {esp.especialidad}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">
                          {esp.total}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-green-600">
                          {esp.en_base}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-500">
                          {esp.no_en_base}
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-indigo-600">
                          {esp.porcentaje.toFixed(2)}%
                        </td>
                        <td className="px-3 py-2 lg:px-4 lg:py-3 text-center">
                          {getEstadoBadge(esp.porcentaje)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies - Análisis de Base de Datos</p>
          <p className="mt-2">Generado: 21 de Enero de 2026</p>
        </div>
      </div>
    </div>
  )
}

// Helper functions to compute table data from filtered medicos

function computeMarcasFromMedicos(
  medicos: MedicoItem[],
): { marca: string; total: number; en_base: number; no_en_base: number; porcentaje: number }[] {
  const map: Record<string, { total: number; en_base: number }> = {}
  for (const m of medicos) {
    if (!map[m.marca]) map[m.marca] = { total: 0, en_base: 0 }
    map[m.marca].total++
    if (m.en_base) map[m.marca].en_base++
  }
  return Object.entries(map)
    .map(([marca, s]) => ({
      marca,
      total: s.total,
      en_base: s.en_base,
      no_en_base: s.total - s.en_base,
      porcentaje: s.total > 0 ? (s.en_base / s.total) * 100 : 0,
    }))
    .sort((a, b) => b.total - a.total)
}

function computePaisesFromMedicos(
  medicos: MedicoItem[],
): { pais: string; total: number; en_base: number; no_en_base: number; porcentaje: number }[] {
  const map: Record<string, { total: number; en_base: number }> = {}
  for (const m of medicos) {
    if (!map[m.pais]) map[m.pais] = { total: 0, en_base: 0 }
    map[m.pais].total++
    if (m.en_base) map[m.pais].en_base++
  }
  return Object.entries(map)
    .map(([pais, s]) => ({
      pais,
      total: s.total,
      en_base: s.en_base,
      no_en_base: s.total - s.en_base,
      porcentaje: s.total > 0 ? (s.en_base / s.total) * 100 : 0,
    }))
    .sort((a, b) => b.total - a.total)
}

function computeEspecialidadesFromMedicos(
  medicos: MedicoItem[],
): {
  especialidad: string
  total: number
  en_base: number
  no_en_base: number
  porcentaje: number
}[] {
  const map: Record<string, { total: number; en_base: number }> = {}
  for (const m of medicos) {
    if (!map[m.especialidad]) map[m.especialidad] = { total: 0, en_base: 0 }
    map[m.especialidad].total++
    if (m.en_base) map[m.especialidad].en_base++
  }
  return Object.entries(map)
    .map(([especialidad, s]) => ({
      especialidad,
      total: s.total,
      en_base: s.en_base,
      no_en_base: s.total - s.en_base,
      porcentaje: s.total > 0 ? (s.en_base / s.total) * 100 : 0,
    }))
    .sort((a, b) => b.total - a.total)
}
